import { useCallback, useEffect, useState } from 'react';
import LoadingWrapper from 'components/LoadingWrapper';
import {
  THubStudentSettingsState,
  fetchHubStudentSettings,
  hubStudentSettingsLoadingSelector,
  hubStudentSettingsSelector,
  hubStudentSubscribeSettingOnChange,
  saveHubStudentSettings,
} from 'slices/hubStudentSettings.slice';
import { organizationSelector } from 'slices/organization.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import {
  ViewerOptions,
  requestViewerPages,
  viewerPagesSelector,
} from 'slices/viewerPages.slice';
import { EAppId, TOrganization, getRedirectUri } from '@timeedit/types';
import { EAppEnv } from '@timeedit/types/lib/enums/appEnv.enum';
import {
  Alert,
  Button,
  DefaultContentLayout,
  Segmented,
  Select,
  TEInput,
  Typography,
} from '@timeedit/ui-components';
import { Col, Input, Row } from '@timeedit/ui-components/antd';
import intl from '../../../i18n/intl';
import { filterOptionLowerCaseInclude } from '../allocateSettings/ViewerPage';

const language = intl.messages as Record<string, string>;

const HubStudentSubscribeSettingsPage = () => {
  const dispatch = useAppDispatch();
  const appEnv =
    (import.meta.env.VITE_TE_APP_ENV as EAppEnv) ?? EAppEnv.PRODUCTION;
  const organization = useAppSelector(organizationSelector);
  const config = useAppSelector(hubStudentSettingsSelector);
  const configLoading = useAppSelector(hubStudentSettingsLoadingSelector);
  const pages = useAppSelector(viewerPagesSelector);
  const [pageValue, setPageValue] = useState<string>('');

  useEffect(() => {
    dispatch(fetchHubStudentSettings());
  }, []);

  useEffect(() => {
    dispatch(requestViewerPages(organization?.customerSignature));
  }, [organization]);

  useEffect(() => {
    if (!isValid(config, organization)) {
      setPageValue('');
      return;
    }
    setPageValue(
      `${config?.studentSubscribeSettings?.path}${config?.studentSubscribeSettings?.page}`
    );
  }, [config]);

  const onSave = useCallback(() => dispatch(saveHubStudentSettings()), []);

  const isLoading = configLoading;

  function presentLink() {
    if (!isValid(config, organization)) {
      return 'No valid link';
    }
    const base = getRedirectUri(
      EAppId.TE_VIEWER,
      organization?.customerSignature ?? '',
      appEnv
    );
    const link = `${base}/${config?.studentSubscribeSettings?.path}/r.html?h=t&sid=${config?.studentSubscribeSettings?.page}`;
    return (
      <span>
        Viewer link{' '}
        <a href={link} target="_blank" rel="noreferrer">
          {link}
        </a>
      </span>
    );
  }

  return (
    <DefaultContentLayout
      title="Subscribe"
      subtitle="Configure iCal-subscription for students"
      actions={
        <Button type="primary" onClick={onSave}>
          {language.save_changes}
        </Button>
      }
    >
      <LoadingWrapper isLoading={isLoading}>
        {!isLoading && (
          <div style={{ margin: '10px' }}>
            <TEInput.LabelWrapper
              label={'Subscribe for students'}
              tooltip={'Enable to display subscribe for students'}
            >
              <div>
                <Segmented<boolean>
                  value={config?.studentSubscribeSettings?.enabled}
                  options={[
                    {
                      value: false,
                      label: language.disabled,
                    },
                    {
                      value: true,
                      label: language.enabled,
                    },
                  ]}
                  onChange={(value) =>
                    dispatch(
                      hubStudentSubscribeSettingOnChange({
                        path: 'enabled',
                        value: value,
                      })
                    )
                  }
                />
              </div>
            </TEInput.LabelWrapper>
            <Typography.Title className="row-box" level={4}>
              {'Link to Viewer'}
            </Typography.Title>
            <Row align="middle" className="row-box">
              <Col>{'Page'}&nbsp; </Col>
              <Col className="space-after">
                <Select<string, ViewerOptions>
                  className="page-dropdown"
                  showSearch
                  placeholder="Select a page"
                  onChange={(value, option) => {
                    setPageValue(value);
                    if (!option) {
                      return;
                    }
                    dispatch(
                      hubStudentSubscribeSettingOnChange({
                        path: 'path',
                        value: `${option['path']}`,
                      })
                    );
                    dispatch(
                      hubStudentSubscribeSettingOnChange({
                        path: 'page',
                        value: `${option['id']}`,
                      })
                    );
                  }}
                  filterOption={filterOptionLowerCaseInclude}
                  options={pages}
                  value={pageValue}
                  popupMatchSelectWidth={false}
                />
              </Col>
              <Col>
                <Input
                  size="small"
                  variant="filled"
                  value={config?.studentSubscribeSettings?.path}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    dispatch(
                      hubStudentSubscribeSettingOnChange({
                        path: 'path',
                        value: e.target.value,
                      })
                    )
                  }
                />
              </Col>
              <Col>
                <Input
                  size="small"
                  variant="filled"
                  value={config?.studentSubscribeSettings?.page}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    dispatch(
                      hubStudentSubscribeSettingOnChange({
                        path: 'page',
                        value: e.target.value,
                      })
                    )
                  }
                />
              </Col>
            </Row>
            <p className="row-box">{presentLink()}</p>

            <Alert
              className="help-text"
              description={language.hubViewerLinkHelp}
              type="info"
              showIcon={false}
            />
          </div>
        )}
      </LoadingWrapper>
    </DefaultContentLayout>
  );
};

function isValid(
  settings: THubStudentSettingsState['config'],
  organization: TOrganization | undefined
) {
  return (
    stringHasContent(organization?.customerSignature) &&
    stringHasContent(settings?.studentSubscribeSettings?.page) &&
    stringHasContent(settings?.studentSubscribeSettings?.path)
  );
}

function stringHasContent(string: string | undefined): boolean {
  if (!string) return false;
  return string.length > 0;
}

export default HubStudentSubscribeSettingsPage;
